import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";

import I18nText from "../../../elements/I18nText";
import FormattedDate from "../../../elements/FormattedDate";
import { SecondaryButton } from "../../../elements/Button";
import CommentListDialog from "./CommentListDialog";

const PREVIEW_MAX_HEIGHT = 220;
const FADE_HEIGHT = 48;

const useStyles = makeStyles(theme => ({
  list: {
    maxHeight: PREVIEW_MAX_HEIGHT,
    overflow: "hidden",
    position: "relative"
  },
  row: {
    cursor: "pointer",
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  fade: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: FADE_HEIGHT,
    background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
    pointerEvents: "none"
  }
}));

const CommentSummary = ({ comments }) => {
  const classes = useStyles();
  const [showAll, setShowAll] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const listRef = useRef(null);

  const sortedComments = [...comments].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  useLayoutEffect(() => {
    const el = listRef.current;

    if (!el) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = () =>
      setHasOverflow(el.scrollHeight > el.clientHeight + 1);

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);

    return () => observer.disconnect();
  }, [sortedComments.length]);

  if (comments.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        <I18nText>devices.status.comment.noData</I18nText>
      </Typography>
    );
  }

  return (
    <>
      {showAll && (
        <CommentListDialog
          comments={sortedComments}
          onClose={() => setShowAll(false)}
        />
      )}

      <Box
        ref={listRef}
        className={classes.list}
        display="flex"
        flexDirection="column"
        style={{ gap: 12 }}
      >
        {sortedComments.map(comment => (
          <Box
            key={comment.id}
            className={classes.row}
            padding={0.5}
            onClick={() => setShowAll(true)}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              fontSize={12}
              color="text.secondary"
            >
              <span>{comment.author || "system"}</span>
              <FormattedDate>{comment.date}</FormattedDate>
            </Box>
            <Box
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical"
              }}
            >
              {comment.comment}
            </Box>
          </Box>
        ))}

        {hasOverflow && <Box className={classes.fade} />}
      </Box>

      {hasOverflow && (
        <Box marginTop={1}>
          <SecondaryButton onClick={() => setShowAll(true)}>
            <I18nText values={{ count: sortedComments.length }}>
              devices.status.comment.viewAll
            </I18nText>
          </SecondaryButton>
        </Box>
      )}
    </>
  );
};

export default CommentSummary;
